import { lazy, Suspense, useMemo } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { PagePath } from '@/routes/paths';
import { isMobile } from '@/utils/device';
import { DemoPage } from '@/features/demo/pages';
import { ChatPage } from '@/features/chat-messages/pages/chat';
import { CustomContentWrapper, MainContentWrapper } from '@/layouts/main-layout';

const MobileBlockPage = lazy(() => import('@/features/system/pages/mobile-block'));
// const ErrorPage = lazy(() => import('@/features/system/pages/500'));
const NotFoundPage = lazy(() => import('@/features/system/pages/404'));
const HandleErrorPage = lazy(() => import('@/features/system/pages/handle-error'));
const MainLayout = lazy(() => import('@/layouts/main-layout'));
const BlankLayout = lazy(() => import('@/layouts/blank-layout'));
const NewRequestPage = lazy(() => import('@/features/new-requests/pages/index'));
const SupplierDetailPage = lazy(() => import('@/features/supplier-detail/pages'));
const AccountPage = lazy(() => import('@/features/accounts/pages/index'));
const ContactUsPage = lazy(() => import('@/features/contact-us'));
const ImagePreviewTestPage = lazy(() => import('@/features/test/pages/image-preview-test'));
const InquiryDialogTestPage = lazy(() => import('@/features/test/pages/inquiry-dialog-test'));
const AuthContentExample = lazy(() => import('@/components/auth-content/AuthContentExample'));
const CountryFlagTestPage = lazy(() => import('@/features/test/pages/country-flag-test'));
const InquiriesPage = lazy(() => import('@/features/inquiries/pages'));
const AgentProgressRoutePage = lazy(() => import('@/features/agent-progress/pages/route'));
const AutoRequestPage = lazy(() => import('@/features/auto-request/pages/AutoRequestPage'));
const ReportsPage = lazy(() => import('@features/reports/pages'));
const PreviewIndex = lazy(() => import('@/preview/pages/preview-index'));
const PreviewDetail = lazy(() => import('@/preview/pages/preview-detail'));

export const router = createBrowserRouter([
  {
    path: '/',
    element: <MainLayout />,
    // errorElement: <ErrorPage />,
    // 带菜单布局的页面添加在这个下面
    children: [
      {
        path: PagePath.home,
        element: (
          <Suspense>
            <MainContentWrapper>
              <NewRequestPage />
            </MainContentWrapper>
          </Suspense>
        ),
      },
      {
        path: PagePath.chat,
        element: (
          <Suspense>
            <ChatPage />
          </Suspense>
        ),
      },
      {
        path: PagePath.demo,
        element: (
          <Suspense>
            <MainContentWrapper>
              <DemoPage />
            </MainContentWrapper>
          </Suspense>
        ),
      },
      {
        path: PagePath.account,
        element: (
          <Suspense>
            <MainContentWrapper>
              <AccountPage />
            </MainContentWrapper>
          </Suspense>
        ),
      },
      {
        path: PagePath.inquiries,
        element: (
          <Suspense>
            <CustomContentWrapper>
              <InquiriesPage />
            </CustomContentWrapper>
          </Suspense>
        ),
      },
      {
        path: PagePath.supplier,
        element: (
          <Suspense>
            <CustomContentWrapper>
              <SupplierDetailPage />
            </CustomContentWrapper>
          </Suspense>
        ),
      },
      {
        path: PagePath.supplierDetail,
        element: (
          <Suspense>
            <CustomContentWrapper>
              <SupplierDetailPage />
            </CustomContentWrapper>
          </Suspense>
        ),
      },
      {
        path: PagePath.agentProgress,
        element: (
          <Suspense>
            <CustomContentWrapper>
              <AgentProgressRoutePage />
            </CustomContentWrapper>
          </Suspense>
        ),
      },
      {
        path: PagePath.reports,
        element: (
          <Suspense>
            <CustomContentWrapper>
              <ReportsPage />
            </CustomContentWrapper>
          </Suspense>
        ),
      },
      ...(['dev', 'test'].includes(import.meta.env.MODE)
        ? [
            {
              path: PagePath.testImagePreview,
              element: (
                <Suspense>
                  <MainContentWrapper>
                    <ImagePreviewTestPage />
                  </MainContentWrapper>
                </Suspense>
              ),
            },
            {
              path: PagePath.testInquiryDialog,
              element: (
                <Suspense>
                  <MainContentWrapper>
                    <InquiryDialogTestPage />
                  </MainContentWrapper>
                </Suspense>
              ),
            },
            {
              path: PagePath.testAuthContent,
              element: (
                <Suspense>
                  <MainContentWrapper>
                    <AuthContentExample />
                  </MainContentWrapper>
                </Suspense>
              ),
            },
            {
              path: PagePath.testCountryFlag,
              element: (
                <Suspense>
                  <MainContentWrapper>
                    <CountryFlagTestPage />
                  </MainContentWrapper>
                </Suspense>
              ),
            },
          ]
        : []),
    ],
  },
  // 不需要菜单布局的页面添加在这个下面
  {
    element: <BlankLayout />,
    children: [
      {
        path: PagePath.contactUs,
        element: (
          <Suspense>
            <ContactUsPage />
          </Suspense>
        ),
      },
      {
        // 自动提交落地页：无主布局、免鉴权（匿名可访问）
        path: PagePath.autoRequest,
        element: (
          <Suspense>
            <AutoRequestPage />
          </Suspense>
        ),
      },
    ],
  },
  // Component preview — dev/test only, no auth
  ...(['dev', 'test'].includes(import.meta.env.MODE)
    ? [
        {
          path: PagePath.preview,
          element: (
            <Suspense>
              <PreviewIndex />
            </Suspense>
          ),
        },
        {
          path: PagePath.previewDetail,
          element: (
            <Suspense>
              <PreviewDetail />
            </Suspense>
          ),
        },
      ]
    : []),
  {
    path: PagePath.handleError,
    element: (
      <Suspense>
        <HandleErrorPage />
      </Suspense>
    ),
  },
  {
    path: '*',
    element: (
      <Suspense>
        <NotFoundPage />
      </Suspense>
    ),
  },
]);

export const mobileRoute = createBrowserRouter([
  {
    path: '*',
    element: (
      <Suspense>
        <MobileBlockPage />
      </Suspense>
    ),
  },
]);

export default function AppRouter() {
  const mobile = useMemo(() => isMobile(), []);

  return <RouterProvider router={mobile ? mobileRoute : router} />;
}
